/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { zodResolver } from '@hookform/resolvers/zod';
import { format, isMobile, VariantType } from '@msdyn365-commerce-modules/utilities';
import * as MsDyn365 from '@msdyn365-commerce/core';
import * as React from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { signUp_DLX_V2Async, signUp_DLXAsync } from '../../actions/DataActionExtension.g';
import PhoneInputComponent from '../../common/components/phone-number-input';
import { getLoyaltySchema } from '../../schema/loyalty-store-sign-up';
import { TEMPORARY_ACCOUNT_NUMBER_LENGTH } from '../../themes/venchi/utils/constants';
import {
    determineSiteLanguageForAbandonedCart,
    getSiteLanguageConfigFromContext
} from '../../themes/venchi/utils/determineSiteLanguageForAbandonedCart';
import { textWithLineBreaks } from '../../themes/venchi/utils/textWithLineBreaks';
import { ILoyaltyStoreSignUpViewProps } from './loyalty-store-sign-up';

// Define the component
const LoyaltyStoreSignUpView: React.FC<ILoyaltyStoreSignUpViewProps> = props => {
    // Destructure props for easier access
    const {
        loyaltyStoreSignUpMandatoryAgreement,
        additionalFieldsHeading,
        birthdaySubtext,
        postalCodeSubtext,
        phoneNumberSubtext,
        // loyaltyVCLUBDetailsLinkText,
        feedbackAlreadySubscribed,
        feedbackCardBeingGenerated,
        feedbackSuccess,
        privacyPolicyURL,
        textGenericMarketing,
        // walletLoyaltyProgramName,
        loyaltyCouponCode,
        loyaltyBarcodeImage,
        couponCodeText,
        showFlagGenericMarketing,
        showFlagProfiling,
        showFlagThirdParty
    } = props.config;

    const {
        vClubCard,
        joinedLoyaltyThankYouHeader,
        loyaltyStoreSignUpSubmitButtonLabel,
        loyaltyRegistrationDateText,
        htmlGenericMarketing,
        htmlProfiling,
        htmlThirdParty,
        birthdayLabelText,
        addressZipCodeLabelOptional,
        phoneLabelText,
        emailAddressLabelText,
        emailErrortext,
        feedbackErrorText,
        loyaltyBarcodeText
    } = props.resources;

    const siteLanguageConfig = getSiteLanguageConfigFromContext(props.context);
    const localeString = siteLanguageConfig.currentLocale;
    const schema = getLoyaltySchema(localeString, !!props.config.enableSignUp);
    type FormInputs = z.infer<ReturnType<typeof getLoyaltySchema>>;

    const onlyGenericMarketingFlag = showFlagGenericMarketing && !showFlagProfiling && !showFlagThirdParty;

    const {
        register,
        handleSubmit,
        formState: { errors, isValid },
        watch
    } = useForm<FormInputs>({
        resolver: zodResolver(schema),
        mode: 'onChange',
        reValidateMode: 'onChange',
        shouldFocusError: true
    });

    // This error is shown under the register button.
    const [phoneErrorMessage, setPhoneErrorMessage] = React.useState('');

    // const programString: string = walletLoyaltyProgramName || 'v-club-emea';

    // State variables for loading, disabled state, and subscription message
    const [loading, setLoading] = React.useState(false);
    const [disabled_state, setDisabled] = React.useState(false);
    const check_ref = React.useRef<HTMLInputElement>(null);
    const [submittedEmailAddress, setSubmittedEmailAddress] = React.useState<string | null>(null);
    const [loyaltyCardNumber, setLoyaltyCardNumber] = React.useState<string | null>(null);
    const [loyaltyEnrollmentDate, setLoyaltyEnrollmentDate] = React.useState<Date | null>(null);
    const [subscribeMessage, setSubscribeMessage] = React.useState({
        state: false,
        message: '',
        class: ''
    });
    const [showPassword, setShowPassword] = React.useState(false);

    const isMobileDevice =
        isMobile({ variant: VariantType.Browser, context: props.context.request }) === 'xs' ||
        isMobile({ variant: VariantType.Viewport, context: props.context.request }) === 'xs' ||
        isMobile({ variant: VariantType.Browser, context: props.context.request }) === 'sm' ||
        isMobile({ variant: VariantType.Viewport, context: props.context.request }) === 'sm';

    // Extract query parameters from the request context
    const query_dict = props.context.request.query as Object;
    const queryParameter = Object.keys(query_dict)[0];
    const store_code = Object.values(query_dict)[0];
    const store_kiosk = Object.keys(query_dict).indexOf('kiosk') > -1 ? true : false;

    const hostname = siteLanguageConfig.hostname;
    const sitePath = props?.context?.request?.sitePath ? props?.context?.request?.sitePath?.substring(1) : '';

    const regionCRM = determineSiteLanguageForAbandonedCart(siteLanguageConfig);

    // Define constants for hostname and locale
    // const loyalty_URL = `https://${hostname}/${sitePath}/loyalty?hasJoined=true`;

    const [unchecked_state, setUncheckedState] = React.useState(localeString !== 'en-us');

    const watchEmail = watch('email');

    // Function to handle form submission
    const _submit = async (data: any) => {
        if (phoneErrorMessage) {
            return;
        }

        setDisabled(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        setSubmittedEmailAddress(data.email);
        setLoading(true);
        let birthday_slash: string = data.birthday;
        birthday_slash = birthday_slash.replace('-', '/').replace('-', '/');
        let message: string;

        const context = props.context;

        if (!props.config.enableSignUp) {
            signUp_DLXAsync(
                { callerContext: context.actionContext },
                '',
                '',
                data.email,
                localeString !== 'en-us' ? data.phone_number : `+1${data.phone_number}`,
                true,
                data.marketing,
                onlyGenericMarketingFlag ? data.marketing : data.profiling,
                onlyGenericMarketingFlag ? false : data.thirdparty,
                localeString,
                birthday_slash,
                data.postal_code,
                store_code ? `Store_${store_code}` : 'Store_fallback',
                regionCRM
            )
                .then(result => {
                    if (result.Error !== '') {
                        throw new Error(result.Error);
                    } else if (result.CustomerGUID?.length !== 0) {
                        console.log('New email');
                        // Brand new email
                        message = feedbackSuccess ?? 'Thank you! Please check your email';
                        setSubscribeMessage({
                            state: true,
                            message: message,
                            class: 'success'
                        });
                        // If the following array is NOT empty and OOUID is NOT 0, the synced user did NOT have a Loyalty card yet
                        if (result.LoyaltyCards && result.LoyaltyCards.length > 0 && result.LoyaltyCards[0].OmOperatingUnitId !== 0) {
                            if (result.LoyaltyCards[0].CardNumber?.length) {
                                console.log('Loyalty card issued');
                                setLoyaltyCardNumber(result.LoyaltyCards[0].CardNumber ?? null);
                                setLoyaltyEnrollmentDate(result.LoyaltyCards[0].LoyaltyEnrollmentDateLocal ?? null);
                            }
                            // Old email, new loyalty
                            message = feedbackSuccess ?? 'Thank you! Please check your email';
                            setSubscribeMessage({
                                state: true,
                                message: message,
                                class: 'success'
                            });
                        } else {
                            console.log('Loyalty card already existing');
                            // Synced user with an existing loyalty card
                            throw new Error('already_existing');
                        }
                    } else if (result.AccountNum?.length === TEMPORARY_ACCOUNT_NUMBER_LENGTH) {
                        console.log('Account not synced yet');
                        // This user is not synced yet, so they still have a temporary GUID, e.g. 278ef388-f813-449b-b7ac-721169659675
                        // New email but already generating the loyalty
                        throw new Error('generating');
                    } else if (result.AccountNum?.length === 10) {
                        console.log('Account synced');
                        // This user is synced already, so they have an FO account number, e.g. FC12345678
                        // If the following array is NOT empty and OOUID is NOT 0, the synced user did NOT have a Loyalty card yet
                        if (result.LoyaltyCards && result.LoyaltyCards.length > 0 && result.LoyaltyCards[0].OmOperatingUnitId !== 0) {
                            if (result.LoyaltyCards[0].CardNumber?.length) {
                                console.log('Loyalty card issued');
                                setLoyaltyCardNumber(result.LoyaltyCards[0].CardNumber ?? null);
                                setLoyaltyEnrollmentDate(result.LoyaltyCards[0].LoyaltyEnrollmentDateLocal ?? null);
                            }
                            // Old email, new loyalty
                            message = feedbackSuccess ?? 'Thank you! Please check your email';
                            setSubscribeMessage({
                                state: true,
                                message: message,
                                class: 'success'
                            });
                        } else {
                            console.log('Loyalty card already existing');
                            // Synced user with an existing loyalty card
                            throw new Error('already_existing');
                        }
                    }
                    setLoading(false);
                    if (store_kiosk && !result.Error) {
                        setTimeout(() => {
                            window.location.href = `https://${hostname}/${
                                sitePath ? `${sitePath}/` : ''
                            }loyalty-region-selection?${queryParameter}=${store_code}&kiosk=true`;
                        }, 5000);
                    }
                })
                .catch(err => {
                    switch (err.message) {
                        case 'generating':
                            message = feedbackCardBeingGenerated ?? 'Your card is already being generated, thank you for your patience.';
                            setSubscribeMessage({
                                state: true,
                                message: message,
                                class: 'generic'
                            });
                            break;
                        case 'already_existing':
                            message = feedbackAlreadySubscribed ?? 'The inserted email is already subscribed to our loyalty program.';
                            setSubscribeMessage({
                                state: true,
                                message: message,
                                class: 'error'
                            });
                            break;
                        default:
                            message = feedbackErrorText;
                            setSubscribeMessage({
                                state: true,
                                message: message,
                                class: 'error'
                            });
                    }
                    setLoading(false);
                });
        }

        if (props.config.enableSignUp) {
            signUp_DLX_V2Async(
                { callerContext: context.actionContext },
                data.name,
                data.lastname,
                data.email,
                data.password,
                localeString !== 'en-us' ? data.phone_number || '' : `+1${data.phone_number}`,
                true,
                data.marketing,
                onlyGenericMarketingFlag ? data.marketing : data.profiling,
                onlyGenericMarketingFlag ? false : data.thirdparty,
                localeString,
                birthday_slash,
                data.postal_code,
                store_code ? `Store_${store_code}` : 'Store_fallback',
                regionCRM
            )
                .then(result => {
                    console.log(result);
                    if (result.ErrorCode === 'CUSTOMER_EXISTS') {
                        throw new Error('already_existing');
                    }

                    if (result.LoyaltyCards && result.LoyaltyCards.length > 0 && result.LoyaltyCards[0].OmOperatingUnitId !== 0) {
                        if (result.LoyaltyCards[0].CardNumber?.length) {
                            console.log('Loyalty card issued');
                            setLoyaltyCardNumber(result.LoyaltyCards[0].CardNumber ?? null);
                            setLoyaltyEnrollmentDate(result.LoyaltyCards[0].LoyaltyEnrollmentDateLocal ?? null);
                        }
                        // Old email, new loyalty
                        message = feedbackSuccess ?? 'Thank you! Please check your email';
                        setSubscribeMessage({
                            state: true,
                            message: message,
                            class: 'success'
                        });
                    } else {
                        console.log('Loyalty card already existing');
                        // Synced user with an existing loyalty card
                        throw new Error('already_existing');
                    }

                    setLoading(false);
                })
                .catch(err => {
                    switch (err.message) {
                        case 'already_existing':
                            message = feedbackAlreadySubscribed ?? 'The inserted email is already subscribed to our loyalty program.';
                            setSubscribeMessage({
                                state: true,
                                message: message,
                                class: 'error'
                            });
                            break;
                        default:
                            message = feedbackErrorText;
                            setSubscribeMessage({
                                state: true,
                                message: message,
                                class: 'error'
                            });
                    }

                    setLoading(false);
                });
        }
    };

    // Debug US 9189
    React.useEffect(() => {
        console.log({ subscribeMessage, loyaltyCardNumber, submittedEmailAddress });
    }, [subscribeMessage, loyaltyCardNumber, submittedEmailAddress]);

    const currentChannelCountryRegionISOCode = props.context.request.channel?.ChannelCountryRegionISOCode || 'IT';

    return (
        <div className='loyalty-store-sign-up-form'>
            {!subscribeMessage.state ? (
                <form onSubmit={handleSubmit(_submit)}>
                    {loading ? <div className={'loading'} style={{ margin: 'auto', marginBottom: '1rem' }} /> : <span></span>}
                    <div className='prominent-fields'>
                        {emailAddressLabelText && emailErrortext ? (
                            <div className='row email-div'>
                                <div className='mandatory-container'>
                                    <label htmlFor='email'>{emailAddressLabelText}</label>
                                    <span className='mandatory-container__red-text'>*{props.resources.mandatoryLabel}</span>
                                </div>
                                <input key='email' name='email' ref={register} />
                                {/* {watchEmail && watchEmail.includes('@') && watchEmail.endsWith('.con') && (
                                    <>
                                        <p className='dotcon'>
                                            Maybe you meant {watchEmail.slice(0, -3)}
                                            <b>com</b>&nbsp;&nbsp;
                                        </p>
                                    </>
                                )} */}
                                {/* For future scalability: Array with forbidden domains and .any in the endsWith? */}
                                {(errors.email || (watchEmail && watchEmail.endsWith('.con'))) && (
                                    <span className='email-error'>{emailErrortext}</span>
                                )}
                            </div>
                        ) : null}

                        {phoneLabelText && localeString === 'en-us' ? (
                            <div className='row phone-number-div' id='phone_number_div'>
                                <div className='mandatory-container'>
                                    <label htmlFor='phone_number_div'>{props.resources.phoneLabelText}</label>
                                    <span className='mandatory-container__red-text'>*{props.resources.mandatoryLabel}</span>
                                </div>
                                <div className='phone-div'>
                                    <div className='phone-prefix'>
                                        <span>+1</span>
                                    </div>
                                    <input
                                        type='tel'
                                        key='phone_number'
                                        id='phone_number'
                                        name='phone_number'
                                        placeholder='(###) ### - ####'
                                        ref={register}
                                        // ref={
                                        //     localeString === 'en-us'
                                        //         ? register({
                                        //               required: true,
                                        //               pattern: /^\\ ( [2-9] {1} [0-9] {2}\\) [ ]? [0-9] {3}[-]?[0-9] {4}$/,
                                        //               maxLength: 15
                                        //           })
                                        //         : register({ required: false, maxLength: 15 })
                                        // }
                                    />
                                </div>
                                {errors.phone_number && (
                                    <span className='phone-error'>{emailErrortext.replace('email', 'phone number')}</span>
                                )}
                                {phoneNumberSubtext ? <p className='optional-subtext'>{phoneNumberSubtext}</p> : null}
                            </div>
                        ) : null}

                        {htmlGenericMarketing && privacyPolicyURL && showFlagGenericMarketing ? (
                            <div className='row checkbox-line agreements'>
                                <input
                                    key='marketing'
                                    type='checkbox'
                                    id='marketing'
                                    name='marketing'
                                    ref={register}
                                    defaultChecked={onlyGenericMarketingFlag}
                                />
                                <label htmlFor='marketing' className='agreements'>
                                    <MsDyn365.RichTextComponent
                                        text={
                                            textGenericMarketing && onlyGenericMarketingFlag
                                                ? textGenericMarketing
                                                : format(htmlGenericMarketing, privacyPolicyURL)
                                        }
                                    />
                                </label>
                            </div>
                        ) : null}

                        {htmlProfiling && privacyPolicyURL && showFlagProfiling ? (
                            <div className='row checkbox-line agreements'>
                                <input id='profiling' key='profiling' type='checkbox' name='profiling' ref={register} />
                                <label htmlFor='profiling' className='agreements'>
                                    <MsDyn365.RichTextComponent text={format(htmlProfiling, privacyPolicyURL)} />
                                </label>
                            </div>
                        ) : null}

                        {htmlThirdParty && privacyPolicyURL && showFlagThirdParty ? (
                            <div className='row checkbox-line agreements'>
                                <input id='thirdparty' key='thirdparty' type='checkbox' name='thirdparty' ref={register} />
                                <label htmlFor='thirdparty' className='agreements thirdparty'>
                                    <MsDyn365.RichTextComponent text={format(htmlThirdParty, privacyPolicyURL)} />
                                </label>
                            </div>
                        ) : null}
                    </div>

                    <div className='secondary-fields'>
                        {additionalFieldsHeading ? <h4>{additionalFieldsHeading}</h4> : null}

                        {props.config.enableSignUp && (
                            <>
                                <div className='row name-div' id='name_div'>
                                    <div className='mandatory-container'>
                                        <label htmlFor='name'>{props.resources.optionalFirstName}</label>
                                        <span className='mandatory-container__red-text'>*{props.resources.mandatoryLabel}</span>
                                    </div>
                                    <input type='text' id='name' name='name' ref={register} />
                                    {/* @ts-ignore */}
                                    {errors.name && <p className='field-error'>{props.resources.addressNameRequiredErrorMessage}</p>}
                                </div>

                                <div className='row lastname-div' id='lastname_div'>
                                    <div className='mandatory-container'>
                                        <label htmlFor='lastname'>{props.resources.optionalLastName}</label>
                                        <span className='mandatory-container__red-text'>*{props.resources.mandatoryLabel}</span>
                                    </div>
                                    <input type='text' key='lastname' id='lastname' name='lastname' ref={register} />
                                    {/* @ts-ignore */}
                                    {errors.lastname && <p className='field-error'>{props.resources.lastnameMandatoryLabel}</p>}
                                </div>

                                <div className='row password-div' id='password_div'>
                                    <div className='mandatory-container'>
                                        <label htmlFor='password'>{props.resources.passwordLabelText}</label>
                                        <span className='mandatory-container__red-text'>*{props.resources.mandatoryLabel}</span>
                                    </div>
                                    <div className='input-group'>
                                        <input
                                            className='input-group__text'
                                            type={showPassword ? 'text' : 'password'}
                                            key='password'
                                            id='password'
                                            name='password'
                                            ref={register}
                                        />
                                        <button
                                            onClick={() => setShowPassword(curValue => !curValue)}
                                            className='input-group__icon-button'
                                            type='button'
                                        >
                                            <i
                                                aria-label={
                                                    showPassword ? props.resources.hidePasswordLabel : props.resources.showPasswordLabel
                                                }
                                                className={`input-group__icon ${
                                                    showPassword ? 'input-group__icon--eye-off' : 'input-group__icon--eye'
                                                }`}
                                            />
                                        </button>
                                    </div>
                                    {/* @ts-ignore */}
                                    {errors.password && (
                                        <p
                                            className='field-error'
                                            dangerouslySetInnerHTML={{ __html: props.resources.passwordRequirementsLabel }}
                                        ></p>
                                    )}
                                </div>
                            </>
                        )}

                        {phoneLabelText && localeString !== 'en-us' ? (
                            <div className='row phone-number-div' id='phone_number_div'>
                                <label htmlFor='phoneInputComponent'>{phoneLabelText}</label>

                                <PhoneInputComponent
                                    channelCountryRegionISOCode={currentChannelCountryRegionISOCode}
                                    isCountryCodeHidden={props.config.hideCountryCodeSelector || false}
                                    phoneInputId={'phoneInputComponent'}
                                    resources={{
                                        phoneValidationErrorMin: props.resources.phoneValidationErrorMin,
                                        phoneValidationErrorMax: props.resources.phoneValidationErrorMax,
                                        phoneValidationErrorNumeric: props.resources.phoneValidationErrorNumeric
                                    }}
                                    onPhoneNumberChange={(phoneNumber: string, fullPhoneNumber: string, hasValidationError: boolean) => {
                                        if (!hasValidationError) {
                                            setPhoneErrorMessage('');
                                        } else {
                                            setPhoneErrorMessage(props.resources.phoneValidationErrorMessage);
                                        }
                                        const phoneNumberHidden: HTMLInputElement = document.getElementById(
                                            'phone_number'
                                        ) as HTMLInputElement;

                                        phoneNumberHidden.value = phoneNumber ? fullPhoneNumber : '';
                                    }}
                                />
                                <input type='hidden' ref={register} id='phone_number' name='phone_number' style={{ display: 'none' }} />
                                {phoneNumberSubtext ? <p className='optional-subtext mt-1'>{phoneNumberSubtext}</p> : null}
                            </div>
                        ) : null}

                        {birthdayLabelText && (
                            <div className='row birthday-div' id='birthday_div'>
                                <label htmlFor='birthday'>{birthdayLabelText}</label>
                                <input type='date' key='birthday' id='birthday' name='birthday' ref={register} />
                                {birthdaySubtext ? <p className='optional-subtext'>{birthdaySubtext}</p> : null}
                            </div>
                        )}

                        {addressZipCodeLabelOptional ? (
                            <div className='row postal-code-div' id='postal_code_div'>
                                <label htmlFor='postal_code'>{addressZipCodeLabelOptional}</label>
                                <input
                                    type='text'
                                    key='postal_code'
                                    id='postal_code'
                                    name='postal_code'
                                    onKeyDown={() => {
                                        if (typeof window !== undefined) {
                                            const this_thing = document.getElementById('postal_code') as HTMLInputElement;
                                            this_thing.maxLength = 9;
                                        }
                                    }}
                                    ref={register}
                                />
                                {postalCodeSubtext ? <p className='optional-subtext'>{postalCodeSubtext}</p> : null}
                            </div>
                        ) : null}
                    </div>

                    {localeString !== 'en-us' ? (
                        // EMEA
                        loyaltyStoreSignUpMandatoryAgreement ? (
                            <div className='row mandatory-agreement-div'>
                                <input
                                    type='checkbox'
                                    className='mandatory-agreement-check'
                                    ref={check_ref}
                                    onClick={() => {
                                        setUncheckedState(!check_ref.current?.checked!);
                                    }}
                                />
                                {/* @ts-ignore */}
                                <label dangerouslySetInnerHTML={{ __html: loyaltyStoreSignUpMandatoryAgreement }} />
                                {/* TODO change to MsDyn365.RichTextComponent to avoid setHTML? */}
                                {/* </input> */}
                            </div>
                        ) : null
                    ) : // US
                    loyaltyStoreSignUpMandatoryAgreement ? (
                        <div className='row mandatory-agreement-div'>
                            {/* TODO change to MsDyn365.RichTextComponent to avoid setHTML? */}
                            <div
                                className='mandatory-agreement agreements'
                                // @ts-ignore
                                dangerouslySetInnerHTML={{ __html: loyaltyStoreSignUpMandatoryAgreement }}
                            ></div>
                        </div>
                    ) : null}

                    <div className='row'>
                        <button
                            type='submit'
                            disabled={!isValid || disabled_state || unchecked_state || !!phoneErrorMessage}
                            className={`status__${subscribeMessage.class} ${
                                !isValid || disabled_state || unchecked_state || !!phoneErrorMessage ? 'btn-disabled' : ''
                            }`}
                        >
                            {loading && loyaltyStoreSignUpSubmitButtonLabel ? (
                                <div className={'loading'} />
                            ) : (
                                <span>{loyaltyStoreSignUpSubmitButtonLabel}</span>
                            )}
                        </button>

                        {phoneErrorMessage && (
                            <div className='ms-sign-up__item-error error itemLevel' role='alert'>
                                <p style={{ color: '#C13533' }}>{phoneErrorMessage}</p>
                            </div>
                        )}
                    </div>
                </form>
            ) : isMobileDevice &&
              loyaltyCardNumber &&
              loyaltyCardNumber.length > 0 &&
              submittedEmailAddress &&
              submittedEmailAddress.length > 0 ? (
                // Here goes the response from the success request
                <div className='loyalty-store-signup-success-container'>
                    <div className={`msc-alert msc-alert-${subscribeMessage.class}`} role='alert'>
                        <div className='loyalty-store-signup-thankyou'>
                            {/* <p>DEBUG test Protip if kiosk error something like show this to a sales assistant in the store</p> */}
                            <div className='message-container'>
                                <p>{joinedLoyaltyThankYouHeader}</p>
                            </div>
                        </div>
                    </div>
                    <div className='qr-code-container'>
                        <img
                            className='qr-code-image'
                            src={`https://qrcodegeneratorloyalty.azurewebsites.net/api/generateqr?qrtext=${loyaltyCardNumber}`}
                            width='115'
                        />
                    </div>
                    <div className='card-number-container'>
                        <p>
                            {vClubCard}: <strong className='loyalty-card-number'>{loyaltyCardNumber}</strong>
                        </p>
                    </div>
                    {loyaltyBarcodeImage?.src && (
                        <div className='barcode-container'>
                            <p className='barcode-text'>{textWithLineBreaks(couponCodeText)}</p>
                            <img className='barcode-image' src={loyaltyBarcodeImage.src} alt={loyaltyCouponCode} />
                            <p className='barcode-text'>{textWithLineBreaks(loyaltyBarcodeText)}</p>
                        </div>
                    )}
                    {/* Hide add to wallet buttons because user creation happens with a delay */}
                    {/* <div className='wallet-buttons-container'>
                        <div className='wallet-button'>
                            <a
                                // eslint-disable-next-line prettier/prettier
                                className='wallet-button-link'
                                href={`https://func-applewallet-prod.azurewebsites.net/venchiloyalty/v1/passes/getpass?program=${programString}&loyaltyCardNumber=${loyaltyCardNumber}&email=${submittedEmailAddress}`}
                                data-msdyn-tracking-id='bada53d825861715766699754'
                            >
                                <img
                                    className='wallet-button-image'
                                    src='https://assets-eur.mkt.dynamics.com/9e61c47c-15ef-44e6-9fdd-26cc7f31a8fd/digitalassets/images/0bdcf2a4-a012-ef11-9f89-6045bd9d2b92?ts=3D638513634711035164'
                                />
                            </a>
                        </div>
                        <div className='wallet-button'>
                            <a
                                // eslint-disable-next-line prettier/prettier
                                className='wallet-button-link'
                                href={`https://func-googlewallet-prod.azurewebsites.net/venchiloyalty/v1/passes/getpass?program=${programString}&loyaltyCardNumber=${loyaltyCardNumber}&email=${submittedEmailAddress}`}
                                data-msdyn-tracking-id='09c9291f628921715766745922'
                            >
                                <img
                                    className='wallet-button-image'
                                    src='https://assets-eur.mkt.dynamics.com/9e61c47c-15ef-44e6-9fdd-26cc7f31a8fd/digitalassets/images/623cf797-a012-ef11-9f89-6045bd9d2b92?ts=3D638513634445092143'
                                />
                            </a>
                        </div>
                    </div> */}
                    <div className='enrollment-date-container'>
                        <p>
                            {loyaltyRegistrationDateText}:{' '}
                            <time className='enrollment-date' dateTime={loyaltyEnrollmentDate?.toISOString()}>
                                {loyaltyEnrollmentDate?.toLocaleDateString(localeString)}
                            </time>
                        </p>
                    </div>
                    {/* {!store_kiosk && loyaltyVCLUBDetailsLinkText && subscribeMessage.class !== 'error' ? (
                                <a href={loyalty_URL}>{loyaltyVCLUBDetailsLinkText}</a>
                            ) : null} */}
                </div>
            ) : (
                <div className={`msc-alert msc-alert-${subscribeMessage.class}`} role='alert'>
                    <div className='message-container'>
                        <p>{subscribeMessage.message}</p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LoyaltyStoreSignUpView;
